'use client'

import { useState } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'

interface CalendarProps {
  selectedDate: Date | null
  onDateSelect: (date: Date) => void
  onMonthChange?: (month: Date) => void
  availableDates?: Set<string> // ISO date strings with availability
  minDate?: Date
  maxDate?: Date
  currentMonth?: Date // Allow parent to control the current month
}

export function Calendar({
  selectedDate,
  onDateSelect,
  onMonthChange,
  availableDates,
  minDate,
  maxDate,
  currentMonth: parentCurrentMonth,
}: CalendarProps) {
  const [internalCurrentMonth, setInternalCurrentMonth] = useState(new Date())

  // Use parent's currentMonth if provided, otherwise use internal state
  const currentMonth = parentCurrentMonth || internalCurrentMonth

  // Get the first day of the month
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1)
  const lastDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0)

  // Get the day of week for the first day (0 = Sunday, 6 = Saturday)
  const firstDayOfWeek = firstDayOfMonth.getDay()

  // Get all days to display in the calendar grid
  const getDaysInMonth = () => {
    const days: (Date | null)[] = []

    // Add empty cells for days before the month starts
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null)
    }

    // Add all days in the month
    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day))
    }

    return days
  }

  const days = getDaysInMonth()
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const isDateDisabled = (date: Date) => {
    if (!date) return true

    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())

    // Check if date is before minDate
    if (minDate) {
      const minDateOnly = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())
      if (dateOnly < minDateOnly) return true
    }

    // Check if date is after maxDate
    if (maxDate) {
      const maxDateOnly = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())
      if (dateOnly > maxDateOnly) return true
    }

    return false
  }

  const isDateSelected = (date: Date) => {
    if (!date || !selectedDate) return false
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    )
  }

  const hasAvailability = (date: Date) => {
    if (!date || !availableDates) return false
    const dateStr = date.toISOString().split('T')[0]
    return availableDates.has(dateStr)
  }

  const handlePreviousMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)

    // If we have a parent controlling the month, let the parent handle the change
    if (parentCurrentMonth && onMonthChange) {
      onMonthChange(newMonth)
    } else {
      // Otherwise, update internal state
      setInternalCurrentMonth(newMonth)
    }
  }

  const handleNextMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)

    // If we have a parent controlling the month, let the parent handle the change
    if (parentCurrentMonth && onMonthChange) {
      onMonthChange(newMonth)
    } else {
      // Otherwise, update internal state
      setInternalCurrentMonth(newMonth)
    }
  }

  const handleDateClick = (date: Date) => {
    if (!date || isDateDisabled(date)) return
    onDateSelect(date)
  }

  const formatMonthYear = () => {
    return currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }

  return (
    <div data-component-name="Calendar" className="w-full">
      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={handlePreviousMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          type="button"
          aria-label="Previous month"
        >
          <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
        </button>
        <h3 className="text-lg font-medium text-gray-900">{formatMonthYear()}</h3>
        <button
          onClick={handleNextMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          type="button"
          aria-label="Next month"
        >
          <ChevronRightIcon className="h-5 w-5 text-gray-600" />
        </button>
      </div>

      {/* Weekday Headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map((day) => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Days */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="h-10" />
          }

          const disabled = isDateDisabled(date)
          const selected = isDateSelected(date)
          const hasSlots = hasAvailability(date)
          const isToday = date.toDateString() === new Date().toDateString()

          return (
            <button
              key={`day-${index}`}
              onClick={() => handleDateClick(date)}
              disabled={disabled}
              type="button"
              className={`
                relative h-10 flex items-center justify-center rounded-lg text-sm
                transition-all duration-150 font-medium
                ${
                  disabled ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 cursor-pointer'
                }
                ${selected ? 'bg-primary text-white hover:bg-primary-hover' : ''}
                ${!selected && !disabled && hasSlots ? 'text-primary font-semibold' : ''}
                ${!selected && !disabled && !hasSlots ? 'text-gray-700' : ''}
                ${isToday && !selected ? 'ring-2 ring-primary ring-opacity-50' : ''}
              `}
            >
              <span>{date.getDate()}</span>
              {hasSlots && !selected && (
                <span className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary rounded-full" />
              )}
            </button>
          )
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-center space-x-6 text-xs">
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-primary rounded" />
          <span className="text-gray-600">Selected</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="relative w-3 h-3">
            <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary rounded-full" />
          </div>
          <span className="text-gray-600">Available</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-gray-200 rounded" />
          <span className="text-gray-600">Unavailable</span>
        </div>
      </div>
    </div>
  )
}
